import React, { useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faChevronDown,
  faChevronUp,
  faToggleOn,
  faToggleOff,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  BulkActionsTrigger,
  BulkActionsPanel,
  BulkActionRow,
  BulkActionIconBox,
  BulkActionTitle,
  BulkActionSubtitle,
  BulkActionsDivider,
  BulkActionsFooter,
} from './styles';
import { LABELS, BULK_AUTO_ACCEPT_COPY } from './constants';

export interface BulkAutoAcceptDropdownProps {
  disabled?: boolean;
  disabledReason?: string;
  affectedCount: number;
  // true when every store in scope already has auto-accept on, false when
  // every one already has it off, null when they're mixed (or scope is
  // empty) — highlights whichever option matches the current uniform state.
  currentState?: boolean | null;
  onSelect: (enable: boolean) => void;
}

const BulkAutoAcceptDropdown: React.FC<BulkAutoAcceptDropdownProps> = ({
  disabled = false,
  disabledReason,
  affectedCount,
  currentState = null,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (enable: boolean) => {
    setOpen(false);
    onSelect(enable);
  };

  if (disabled) {
    return (
      <Tooltip title={disabledReason}>
        <span style={{ cursor: 'not-allowed' }}>
          <BulkActionsTrigger type="button" disabled>
            <FontAwesomeIcon icon={faLayerGroup} />
            {LABELS.BULK_ACTIONS}
            <FontAwesomeIcon icon={faChevronDown} />
          </BulkActionsTrigger>
        </span>
      </Tooltip>
    );
  }

  const content = (
    <BulkActionsPanel>
      <BulkActionRow onClick={() => handleSelect(true)} $active={currentState === true}>
        <BulkActionIconBox $variant="enable">
          <FontAwesomeIcon icon={faToggleOn} />
        </BulkActionIconBox>
        <div>
          <BulkActionTitle>{LABELS.BULK_ENABLE_TITLE}</BulkActionTitle>
          <BulkActionSubtitle>{LABELS.BULK_ENABLE_SUBTITLE}</BulkActionSubtitle>
        </div>
      </BulkActionRow>
      <BulkActionRow onClick={() => handleSelect(false)} $active={currentState === false}>
        <BulkActionIconBox $variant="disable">
          <FontAwesomeIcon icon={faToggleOff} />
        </BulkActionIconBox>
        <div>
          <BulkActionTitle>{LABELS.BULK_DISABLE_TITLE}</BulkActionTitle>
          <BulkActionSubtitle>{LABELS.BULK_DISABLE_SUBTITLE}</BulkActionSubtitle>
        </div>
      </BulkActionRow>
      <BulkActionsDivider />
      <BulkActionsFooter>
        <FontAwesomeIcon icon={faInfoCircle} />
        {BULK_AUTO_ACCEPT_COPY.affectsLabel(affectedCount)}
      </BulkActionsFooter>
    </BulkActionsPanel>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
    >
      <BulkActionsTrigger type="button" $active={open}>
        <FontAwesomeIcon icon={faLayerGroup} />
        {LABELS.BULK_ACTIONS}
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </BulkActionsTrigger>
    </Popover>
  );
};

export default React.memo(BulkAutoAcceptDropdown);
